import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, orderBy, Timestamp, serverTimestamp, getDocs, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from './useUsers';
import { useNotifications } from './useNotifications';
import { Task, CreateTaskData, TaskFilters, TaskStats } from '../types/task';
import { notifyTaskCreated, notifyTaskUpdated, notifyTaskAssigned, notifyTaskCompleted, notifyTaskStatusChanged } from '../services/notificationService';

export const useTasks = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { users } = useUsers();
  const { createNotification, settings } = useNotifications();

  // Helper függvény: undefined értékek kiszűrése a notification data-ból
  const cleanNotificationData = (data: any) => {
    const cleaned: any = {};
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        cleaned[key] = data[key];
      }
    });
    return cleaned;
  };

  // Automatikus feladat szám generálás
  const getNextTaskNumber = async (): Promise<string> => {
    if (!user?.organizationId) {
      throw new Error('No organization ID');
    }

    try {
      // Keresés a legmagasabb feladat számra az adott szervezetben
      const q = query(
        collection(db, 'tasks'),
        where('organizationId', '==', user.organizationId),
        orderBy('taskNumber', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const lastTask = querySnapshot.docs[0].data();
        const lastTaskNumber = lastTask.taskNumber;
        
        if (lastTaskNumber) {
          try {
            // Kivesszük a számot a TASK-XXX formátumból
            const numberPart = parseInt(lastTaskNumber.split('-')[1]);
            const nextNumber = numberPart + 1;
            return `TASK-${nextNumber.toString().padStart(3, '0')}`;
          } catch (error) {
            console.error('Error parsing task number:', error);
            return `TASK-001`;
          }
        }
      }
      
      // Ha nincs korábbi feladat, az első számmal kezdünk
      return `TASK-001`;
    } catch (error) {
      console.error('Error getting next task number:', error);
      // Fallback: véletlenszerű szám
      return `TASK-${Math.floor(Math.random() * 999).toString().padStart(3, '0')}`;
    }
  };

  // Feladatok betöltése
  useEffect(() => {
    if (!user?.organizationId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'tasks'),
      where('organizationId', '==', user.organizationId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const tasksData: Task[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            dueDate: data.dueDate?.toDate() || undefined,
            completedDate: data.completedDate?.toDate() || undefined,
            tags: data.tags || [],
            attachments: data.attachments || [],
            documents: data.documents || [],
            comments: data.comments || []
          } as Task;
        });
        setTasks(tasksData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Error fetching tasks:', error);
        setError('Failed to load tasks');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [user?.organizationId]);

  // Feladat létrehozása
  const createTask = async (taskData: CreateTaskData): Promise<string> => {
    if (!user?.organizationId) {
      throw new Error('No organization ID');
    }

    try {
      // Automatikus feladat szám generálás
      const taskNumber = await getNextTaskNumber();

      // Prepare the data object - filter out undefined values
      const cleanedTaskData: any = {};
      Object.keys(taskData).forEach(key => {
        if (taskData[key as keyof CreateTaskData] !== undefined) {
          cleanedTaskData[key] = taskData[key as keyof CreateTaskData];
        }
      });

      const dataToSave: any = {
        ...cleanedTaskData,
        taskNumber,
        organizationId: user.organizationId,
        createdBy: user.id,
        createdByName: user.displayName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        dueDate: taskData.dueDate ? Timestamp.fromDate(taskData.dueDate) : null,
        tags: taskData.tags || [],
        attachments: taskData.attachments || [],
        documents: taskData.documents || [],
        comments: []
      };

      // Set assignedToName if assignedTo is provided
      if (taskData.assignedTo) {
        const assignedUser = users.find(u => u.id === taskData.assignedTo);
        if (assignedUser) {
          dataToSave.assignedToName = assignedUser.displayName || assignedUser.email;
        }
      }

      const docRef = await addDoc(collection(db, 'tasks'), dataToSave);

      // Értesítés küldése a feladat létrehozásáról
      try {
        // Ha van hozzárendelt személy, értesítjük
        if (taskData.assignedTo && (taskData.assignedTo !== user.id || settings?.debugMode)) {
          await createNotification({
            type: 'task_assigned',
            title: 'Új feladat kijelölve',
            message: `${taskNumber}: ${taskData.title}`,
            data: cleanNotificationData({
              taskId: docRef.id,
              taskNumber: taskNumber,
              projectId: taskData.projectId
            }),
            priority: taskData.priority || 'medium',
            userId: taskData.assignedTo,
            organizationId: user.organizationId,
            createdBy: user.id,
            createdByName: user.displayName,
            actionUrl: `/tasks?task=${docRef.id}`
          });
        }

        // Ha van projekt, értesítjük a projekt csapattagjait
        if (taskData.projectId) {
          // TODO: Projekt csapattagok lekérése és értesítés küldése
        }
      } catch (notificationError) {
        console.error('Error sending task creation notification:', notificationError);
        // Ne akadályozzuk meg a feladat létrehozását, ha az értesítés sikertelen
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  };

  // Feladat frissítése
  const updateTask = async (taskId: string, updates: Partial<CreateTaskData & { actualHours?: number; completedDate?: Date }>): Promise<void> => {
    console.log('useTasks updateTask called with:', { taskId, updates });
    console.log('Current user:', user);
    console.log('User organizationId:', user?.organizationId);
    
    if (!user?.organizationId) {
      console.error('No user or organizationId found');
      throw new Error('No user or organizationId found');
    }
    
    try {
      const taskRef = doc(db, 'tasks', taskId);
      
      // Filter out undefined values from updates
      const cleanedUpdates: any = {};
      Object.keys(updates).forEach(key => {
        if (updates[key as keyof typeof updates] !== undefined) {
          cleanedUpdates[key] = updates[key as keyof typeof updates];
        }
      });

      const updateData: any = {
        ...cleanedUpdates,
        updatedAt: serverTimestamp()
      };

      // Dátum mezők konvertálása
      if (updates.dueDate) {
        updateData.dueDate = Timestamp.fromDate(updates.dueDate);
      }
      if (updates.completedDate) {
        updateData.completedDate = Timestamp.fromDate(updates.completedDate);
      }

      // Ha a státusz "completed"-re változott, állítsuk be a befejezési dátumot
      if (updates.status === 'completed' && !updates.completedDate) {
        updateData.completedDate = serverTimestamp();
      }

      // Set assignedToName if assignedTo is provided
      if (updates.assignedTo) {
        const assignedUser = users.find(u => u.id === updates.assignedTo);
        if (assignedUser) {
          updateData.assignedToName = assignedUser.displayName || assignedUser.email;
        }
      } else if (updates.assignedTo === null || updates.assignedTo === undefined) {
        // If assignedTo is explicitly cleared, also clear assignedToName
        updateData.assignedToName = null;
      }

      console.log('Updating task in Firestore with data:', updateData);
      await updateDoc(taskRef, updateData);
      console.log('Task updated successfully in Firestore');

      // Értesítés küldése a feladat frissítéséről
      try {
        const currentTask = tasks.find(t => t.id === taskId);
        if (currentTask) {
          // Ha a státusz "completed"-re változott, értesítjük az érintetteket
          if (updates.status === 'completed') {
            // Értesítjük a feladat létrehozóját (ha nem mi magunk vagyuk vagy debug mód be van kapcsolva)
            if (currentTask.createdBy && (currentTask.createdBy !== user.id || settings?.debugMode)) {
              await createNotification({
                type: 'task_completed',
                title: 'Feladat befejezve',
                message: `${currentTask.taskNumber}: ${currentTask.title}`,
                data: cleanNotificationData({
                  taskId: taskId,
                  taskNumber: currentTask.taskNumber,
                  projectId: currentTask.projectId
                }),
                priority: 'medium',
                userId: currentTask.createdBy,
                organizationId: user.organizationId,
                createdBy: user.id,
                createdByName: user.displayName,
                actionUrl: `/tasks?task=${taskId}`
              });
            }
          }
          
          // Ha van hozzárendelt személy, értesítjük
          if (currentTask.assignedTo && (currentTask.assignedTo !== user.id || settings?.debugMode)) {
            await createNotification({
              type: 'task_assigned',
              title: 'Feladat frissítve',
              message: `${currentTask.taskNumber}: ${currentTask.title}`,
              data: cleanNotificationData({
                taskId: taskId,
                taskNumber: currentTask.taskNumber,
                projectId: currentTask.projectId
              }),
              priority: 'medium',
              userId: currentTask.assignedTo,
              organizationId: user.organizationId,
              createdBy: user.id,
              createdByName: user.displayName,
              actionUrl: `/tasks?task=${taskId}`
            });
          }

          // Ha új személyt rendeltek hozzá, értesítjük
          if (updates.assignedTo && (updates.assignedTo !== user.id || settings?.debugMode) && updates.assignedTo !== currentTask.assignedTo) {
            await createNotification({
              type: 'task_assigned',
              title: 'Új feladat kijelölve',
              message: `${currentTask.taskNumber}: ${currentTask.title}`,
              data: cleanNotificationData({
                taskId: taskId,
                taskNumber: currentTask.taskNumber,
                projectId: currentTask.projectId
              }),
              priority: currentTask.priority || 'medium',
              userId: updates.assignedTo,
              organizationId: user.organizationId,
              createdBy: user.id,
              createdByName: user.displayName,
              actionUrl: `/tasks?task=${taskId}`
            });
          }
        }
      } catch (notificationError) {
        console.error('Error sending task update notification:', notificationError);
        // Ne akadályozzuk meg a frissítést, ha az értesítés sikertelen
      }
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  };

  // Feladat törlése
  const deleteTask = async (taskId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'tasks', taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  };

  // Feladat státusz változtatása
  const updateTaskStatus = async (taskId: string, status: Task['status']): Promise<void> => {
    const updates: any = { status };
    
    if (status === 'completed') {
      updates.completedDate = new Date();
    }
    
    await updateTask(taskId, updates);
  };

  // Feladat prioritás változtatása
  const updateTaskPriority = async (taskId: string, priority: Task['priority']): Promise<void> => {
    await updateTask(taskId, { priority });
  };

  // Feladat hozzárendelése felhasználóhoz
  const assignTask = async (taskId: string, userId: string, userName: string): Promise<void> => {
    await updateTask(taskId, {
      assignedTo: userId,
      assignedToName: userName
    });
  };

  // Feladat hozzárendelés eltávolítása
  const unassignTask = async (taskId: string): Promise<void> => {
    const taskRef = doc(db, 'tasks', taskId);
    await updateDoc(taskRef, {
      assignedTo: null,
      assignedToName: null,
      updatedAt: serverTimestamp()
    });
  };

  // Feladatok szűrése
  const filterTasks = (filters: TaskFilters): Task[] => {
    return tasks.filter(task => {
      // Státusz szűrő
      if (filters.status && task.status !== filters.status) {
        return false;
      }

      // Prioritás szűrő
      if (filters.priority && task.priority !== filters.priority) {
        return false;
      }

      // Felelős szűrő
      if (filters.assignedTo && task.assignedTo !== filters.assignedTo) {
        return false;
      }

      // Ügyfél szűrő
      if (filters.customer && task.customerId !== filters.customer) {
        return false;
      }

      // Projekt szűrő
      if (filters.project && task.projectId !== filters.project) {
        return false;
      }

      // Határidő szűrő
      if (filters.dueDateRange) {
        const { start, end } = filters.dueDateRange;
        if (task.dueDate) {
          if (start && task.dueDate < start) return false;
          if (end && task.dueDate > end) return false;
        }
      }

      // Címke szűrő
      if (filters.tags && filters.tags.length > 0) {
        const hasMatchingTag = filters.tags.some(tag => task.tags.includes(tag));
        if (!hasMatchingTag) return false;
      }

      // Feladat szám szűrő
      if (filters.taskNumber) {
        const taskNumber = task.taskNumber || '';
        if (!taskNumber.toLowerCase().includes(filters.taskNumber.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  };

  // Feladat statisztikák
  const getTaskStats = (): TaskStats => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000));

    const stats: TaskStats = {
      total: tasks.length,
      todo: tasks.filter(t => t.status === 'todo').length,
      inProgress: tasks.filter(t => t.status === 'in_progress').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      cancelled: tasks.filter(t => t.status === 'cancelled').length,
      overdue: tasks.filter(t => t.dueDate && t.dueDate < now && t.status !== 'completed').length,
      dueToday: tasks.filter(t => t.dueDate && t.dueDate >= today && t.dueDate < new Date(today.getTime() + 24 * 60 * 60 * 1000)).length,
      dueThisWeek: tasks.filter(t => t.dueDate && t.dueDate >= thisWeek && t.dueDate < new Date(thisWeek.getTime() + 7 * 24 * 60 * 60 * 1000)).length
    };

    return stats;
  };

  // Saját feladatok lekérése
  const getMyTasks = (): Task[] => {
    return tasks.filter(task => task.assignedTo === user?.id);
  };

  // Általam létrehozott feladatok lekérése
  const getCreatedByMe = (): Task[] => {
    return tasks.filter(task => task.createdBy === user?.id);
  };

  // Komment hozzáadása fájlokkal
  const addComment = async (taskId: string, message: string, files?: File[]): Promise<void> => {
    if (!user?.organizationId) {
      throw new Error('No organization ID');
    }

    try {
      const taskRef = doc(db, 'tasks', taskId);
      const currentTask = tasks.find(t => t.id === taskId);
      
      if (!currentTask) {
        throw new Error('Task not found');
      }

      const commentId = `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Fájlok feltöltése, ha vannak
      let attachments: any[] = [];
      if (files && files.length > 0) {
        const { uploadCommentAttachment } = await import('../services/taskCommentAttachmentService');
        
        for (const file of files) {
          const attachment = await uploadCommentAttachment(taskId, commentId, file, user.organizationId);
          attachments.push(attachment);
        }
      }

      const newComment = {
        id: commentId,
        taskId,
        userId: user.id,
        userName: user.displayName || user.email,
        message,
        attachments: attachments.length > 0 ? attachments : undefined,
        createdAt: new Date()
      };

      const updatedComments = [...(currentTask.comments || []), newComment];

      await updateDoc(taskRef, {
        comments: updatedComments,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  };

  // Komment törlése (mellékletekkel együtt)
  const deleteComment = async (taskId: string, commentId: string): Promise<void> => {
    if (!user?.organizationId) {
      throw new Error('No organization ID');
    }

    try {
      const taskRef = doc(db, 'tasks', taskId);
      const currentTask = tasks.find(t => t.id === taskId);
      
      if (!currentTask) {
        throw new Error('Task not found');
      }

      // Komment keresése a mellékletek törléséhez
      const comment = currentTask.comments?.find(c => c.id === commentId);
      
      // Mellékletek törlése Storage-ból
      if (comment?.attachments && comment.attachments.length > 0) {
        const { deleteCommentAttachment } = await import('../services/taskCommentAttachmentService');
        
        for (const attachment of comment.attachments) {
          try {
            await deleteCommentAttachment(taskId, commentId, attachment.id, user.organizationId);
          } catch (error) {
            console.error('Error deleting attachment:', error);
            // Folytatjuk a többi melléklet törlésével
          }
        }
      }

      const updatedComments = (currentTask.comments || []).filter(c => c.id !== commentId);

      await updateDoc(taskRef, {
        comments: updatedComments,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  };

  // Komment szerkesztése
  const updateComment = async (taskId: string, commentId: string, message: string): Promise<void> => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      const currentTask = tasks.find(t => t.id === taskId);
      
      if (!currentTask) {
        throw new Error('Task not found');
      }

      const updatedComments = (currentTask.comments || []).map(c => 
        c.id === commentId ? { ...c, message, updatedAt: new Date() } : c
      );

      await updateDoc(taskRef, {
        comments: updatedComments,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  };

  // Komment melléklet törlése
  const deleteCommentAttachment = async (taskId: string, commentId: string, attachmentId: string): Promise<void> => {
    if (!user?.organizationId) {
      throw new Error('No organization ID');
    }

    try {
      const taskRef = doc(db, 'tasks', taskId);
      const currentTask = tasks.find(t => t.id === taskId);
      
      if (!currentTask) {
        throw new Error('Task not found');
      }

      // Storage-ból törlés
      const { deleteCommentAttachment: deleteFromStorage } = await import('../services/taskCommentAttachmentService');
      await deleteFromStorage(taskId, commentId, attachmentId, user.organizationId);

      // Firestore frissítése
      const updatedComments = (currentTask.comments || []).map(comment => {
        if (comment.id === commentId && comment.attachments) {
          return {
            ...comment,
            attachments: comment.attachments.filter(a => a.id !== attachmentId),
            updatedAt: new Date()
          };
        }
        return comment;
      });

      await updateDoc(taskRef, {
        comments: updatedComments,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting comment attachment:', error);
      throw error;
    }
  };

  // Feladat exportálása szöveges formátumban (kommentekkel)
  const exportTaskAsText = (task: Task): string => {
    const lines: string[] = [];
    
    lines.push('='.repeat(80));
    lines.push(`FELADAT: ${task.taskNumber} - ${task.title}`);
    lines.push('='.repeat(80));
    lines.push('');
    
    // Alapadatok
    lines.push('ALAPADATOK:');
    lines.push(`Státusz: ${task.status}`);
    lines.push(`Prioritás: ${task.priority}`);
    lines.push(`Létrehozva: ${task.createdAt.toLocaleString('hu-HU')}`);
    lines.push(`Létrehozó: ${task.createdByName || 'Ismeretlen'}`);
    
    if (task.assignedToName) {
      lines.push(`Felelős: ${task.assignedToName}`);
    }
    
    if (task.dueDate) {
      lines.push(`Határidő: ${task.dueDate.toLocaleString('hu-HU')}`);
    }
    
    if (task.customerName) {
      lines.push(`Ügyfél: ${task.customerName}`);
    }
    
    lines.push('');
    
    // Leírás
    if (task.description) {
      lines.push('LEÍRÁS:');
      lines.push(task.description);
      lines.push('');
    }
    
    // Címkék
    if (task.tags && task.tags.length > 0) {
      lines.push('CÍMKÉK:');
      lines.push(task.tags.join(', '));
      lines.push('');
    }
    
    // Kommentek
    if (task.comments && task.comments.length > 0) {
      lines.push('MEGJEGYZÉSEK / TESZTELÉSI EREDMÉNYEK:');
      lines.push('-'.repeat(80));
      
      task.comments.forEach((comment, index) => {
        let commentDate: Date;
        
        // Firestore Timestamp kezelése
        if (comment.createdAt && typeof comment.createdAt === 'object' && 'toDate' in comment.createdAt) {
          commentDate = (comment.createdAt as any).toDate();
        } else if (comment.createdAt instanceof Date) {
          commentDate = comment.createdAt;
        } else {
          commentDate = new Date(comment.createdAt);
        }
        
        const formattedDate = new Intl.DateTimeFormat('hu-HU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).format(commentDate);
        
        lines.push(`\n[${index + 1}] ${comment.userName} - ${formattedDate}`);
        lines.push(comment.message);
        lines.push('-'.repeat(80));
      });
      
      lines.push('');
    }
    
    lines.push('');
    lines.push('='.repeat(80));
    
    return lines.join('\n');
  };

  // Több feladat exportálása
  const exportTasksAsText = (tasksToExport: Task[]): string => {
    return tasksToExport.map(task => exportTaskAsText(task)).join('\n\n\n');
  };

  // Export fájl letöltése
  const downloadTasksAsText = (tasksToExport: Task[], filename?: string): void => {
    const text = exportTasksAsText(tasksToExport);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `feladatok_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    updateTaskPriority,
    assignTask,
    unassignTask,
    filterTasks,
    getTaskStats,
    getMyTasks,
    getCreatedByMe,
    addComment,
    deleteComment,
    updateComment,
    deleteCommentAttachment,
    exportTaskAsText,
    exportTasksAsText,
    downloadTasksAsText
  };
};